import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Copy, Check, Eye, EyeOff, AlertCircle } from 'lucide-react'

interface AdminGroup {
  id: string
  name: string
}

interface AddDeviceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeviceCreated: () => void
}

export function AddDeviceDialog({ open, onOpenChange, onDeviceCreated }: AddDeviceDialogProps) {
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<string>('')
  const [adminGroups, setAdminGroups] = useState<AdminGroup[]>([])
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Success state
  const [createdDevice, setCreatedDevice] = useState<{ deviceId: string; apiKey: string } | null>(null)
  const [showApiKey, setShowApiKey] = useState(false)
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()

  // Load admin groups when dialog opens
  useEffect(() => {
    if (open) {
      loadAdminGroups()
    }
  }, [open])

  const loadAdminGroups = async () => {
    try {
      setLoadingGroups(true)
      const getAdminGroups = apiCallable('getAdminGroups')
      const result = await getAdminGroups({})
      const data = result.data as { success: boolean; groups: AdminGroup[] }

      if (data.success) {
        setAdminGroups(data.groups)
        // Auto-select first group if available
        if (data.groups.length > 0 && !selectedGroupId) {
          setSelectedGroupId(data.groups[0].id)
        }
      }
    } catch (err) {
      console.error('Failed to load admin groups:', err)
      setError(t('addDevice.failedToLoadGroups'))
    } finally {
      setLoadingGroups(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError(t('addDevice.deviceNameRequired'))
      return
    }

    if (!selectedGroupId) {
      setError(t('addDevice.selectGroupRequired'))
      return
    }

    try {
      setSaving(true)
      const createDevice = apiCallable('createDevice')
      const result = await createDevice({
        name: name.trim(),
        location: location.trim() || null,
        groupId: selectedGroupId
      })

      const data = result.data as {
        success: boolean
        deviceId: string
        apiKey: string
        message?: string
      }

      if (data.success) {
        setCreatedDevice({ deviceId: data.deviceId, apiKey: data.apiKey })
        // Don't call onDeviceCreated() here - wait until user clicks Done
      } else {
        setError(data.message || t('addDevice.failedToCreate'))
      }
    } catch (err: any) {
      console.error('Create device error:', err)
      if (err.code === 'failed-precondition') {
        setError(t('addDevice.noPermissionCreate'))
      } else if (err.code === 'permission-denied') {
        setError(t('addDevice.noPermissionGroup'))
      } else {
        setError(t('addDevice.failedToCreate'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleCopyApiKey = async () => {
    if (createdDevice?.apiKey) {
      await navigator.clipboard.writeText(createdDevice.apiKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleClose = () => {
    const wasCreated = createdDevice !== null
    setName('')
    setLocation('')
    setSelectedGroupId(adminGroups.length > 0 ? adminGroups[0].id : '')
    setError('')
    setCreatedDevice(null)
    setShowApiKey(false)
    setCopied(false)
    onOpenChange(false)
    // Refresh parent after modal closes if device was created
    if (wasCreated) {
      onDeviceCreated()
    }
  }

  // No admin groups - show message
  const renderNoAdminGroups = () => (
    <>
      <DialogHeader>
        <DialogTitle>{t('addDevice.cannotAdd')}</DialogTitle>
        <DialogDescription>
          {t('addDevice.needAdmin')}
        </DialogDescription>
      </DialogHeader>

      <div className="py-4">
        <Alert variant="warning">
          <AlertCircle className="h-5 w-5" />
          <AlertTitle>{t('addDevice.createGroupFirst')}</AlertTitle>
          <AlertDescription>{t('addDevice.createGroupHint')}</AlertDescription>
        </Alert>
      </div>

      <DialogFooter>
        <Button onClick={handleClose}>{t('common.close')}</Button>
      </DialogFooter>
    </>
  )

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {loadingGroups ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : adminGroups.length === 0 ? (
          renderNoAdminGroups()
        ) : createdDevice ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('addDevice.deviceCreated')}</DialogTitle>
              <DialogDescription>
                {t('addDevice.saveApiKey')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <Alert variant="warning">
                <AlertCircle className="h-5 w-5" />
                <AlertTitle>{t('addDevice.importantCopy')}</AlertTitle>
                <AlertDescription>{t('addDevice.apiKeyHint')}</AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>{t('addDevice.apiKeyLabel')}</Label>
                <div className="flex gap-2">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={createdDevice.apiKey}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyApiKey}
                  >
                    {copied ? <Check className="h-4 w-4 text-gold" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>{t('common.done')}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('addDevice.title')}</DialogTitle>
              <DialogDescription>
                {t('addDevice.description')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="group">{t('addDevice.group')}</Label>
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('addDevice.selectGroup')} />
                  </SelectTrigger>
                  <SelectContent>
                    {adminGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('addDevice.adminGroupsOnly')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">{t('addDevice.deviceName')}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('addDevice.deviceNamePlaceholder')}
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">{t('addDevice.locationLabel')}</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('addDevice.locationPlaceholder')}
                  disabled={saving}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? t('common.creating') : t('addDevice.createDevice')}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
